import React, { useState, useRef } from "react";
import axios from "axios";
import { API_URL } from "../config";
import { Box, Button, Typography, Stack, Chip } from "@mui/material";
import { styled, keyframes, alpha } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";

// ─── Animations ───────────────────────────────────────────────────────────────
const spin = keyframes`to { transform: rotate(360deg); }`;
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const UploadWrapper = styled(Box)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha("#2563EB", 0.1)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.04)",
  padding: "20px 24px",
  background: "#fff",
  animation: `${fadeUp} 0.45s ease both`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 14px rgba(37,99,235,0.07), 0 8px 28px rgba(0,0,0,0.05)",
  },
}));

const HeaderRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 16,
}));

const IconBox = styled(Box)(() => ({
  width: 34,
  height: 34,
  borderRadius: 9,
  flexShrink: 0,
  background: alpha("#2563EB", 0.1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const PdfDropZone = styled(Box, {
  shouldForwardProp: (p) => p !== "dragover" && p !== "hasfile",
})(({ dragover, hasfile }) => ({
  border: `2px dashed ${
    hasfile ? "#059669" : dragover ? "#2563EB" : alpha("#2563EB", 0.22)
  }`,
  borderRadius: 12,
  padding: "22px 16px",
  textAlign: "center",
  cursor: "pointer",
  background: hasfile
    ? alpha("#059669", 0.03)
    : dragover
    ? alpha("#2563EB", 0.04)
    : alpha("#F0F2F8", 0.7),
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: hasfile ? "#059669" : "#2563EB",
    background: hasfile ? alpha("#059669", 0.05) : alpha("#2563EB", 0.03),
    "& .upload-icon-box": { transform: "translateY(-3px) scale(1.06)" },
  },
}));

const UploadIconBox = styled(Box, {
  shouldForwardProp: (p) => p !== "hasfile",
})(({ hasfile }) => ({
  width: 46,
  height: 46,
  borderRadius: 12,
  margin: "0 auto 10px",
  background: hasfile ? alpha("#059669", 0.1) : alpha("#2563EB", 0.08),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.25s ease",
}));

const UploadBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 9,
  padding: "10px 24px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 12px rgba(37,99,235,0.28)",
  minWidth: 110,
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 18px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const ClearBtn = styled(Button)(() => ({
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  color: "#9CA3AF",
  fontFamily: "'Sora', sans-serif",
  padding: "6px 12px",
  "&:hover": { color: "#DC2626", background: alpha("#DC2626", 0.05) },
}));

const Spinner = styled(Box)(() => ({
  width: 15,
  height: 15,
  border: "2px solid rgba(255,255,255,0.3)",
  borderTop: "2px solid #fff",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
  display: "inline-block",
  flexShrink: 0,
}));

const ProgressBar = styled(Box)(() => ({
  height: 4,
  borderRadius: 2,
  overflow: "hidden",
  background: alpha("#2563EB", 0.1),
  marginTop: 14,
}));

const ProgressFill = styled(Box)(() => ({
  height: "100%",
  borderRadius: 2,
  background: "linear-gradient(90deg, #2563EB, #60A5FA, #2563EB)",
  backgroundSize: "200% 100%",
  animation: `${shimmer} 1.4s infinite linear`,
}));

// ─── Component ────────────────────────────────────────────────────────────────
export default function UploadExamTheoryPDF({ yearId }) {
  const token = JSON.parse(localStorage.getItem("authToken"));
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.type === "application/pdf") setFile(dropped);
  };

  const handleUpload = async () => {
    if (!file) return toast.error("Please select a PDF file first.");
    const formData = new FormData();
    formData.append("file", file);
  

    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}/upload-general-theory-exam-pdf/${yearId}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token ? `Bearer ${token.access}` : "",
          },
        }
      );
      toast.success(res.data.message || "File uploaded successfully.");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      console.error(err.response?.data || err.message);
      toast.error("Error uploading file.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <UploadWrapper>
      {/* ── Header ── */}
      <HeaderRow>
        <IconBox>
          <PictureAsPdfIcon sx={{ fontSize: 17, color: "#2563EB" }} />
        </IconBox>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: "#0F1117",
            fontFamily: "'Sora', sans-serif",
            letterSpacing: "0.1px",
          }}
        >
          Upload Theory PDF
        </Typography>
        {file && (
          <Chip
            label="File ready"
            size="small"
            sx={{
              ml: "auto",
              fontFamily: "'Sora', sans-serif",
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              background: alpha("#059669", 0.1),
              color: "#059669",
              border: `1px solid ${alpha("#059669", 0.22)}`,
            }}
          />
        )}
      </HeaderRow>

      {/* ── Drop zone ── */}
      <PdfDropZone
        dragover={dragOver ? 1 : 0}
        hasfile={file ? 1 : 0}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {file ? (
          /* File selected */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.6,
            }}
          >
            <UploadIconBox className="upload-icon-box" hasfile={1}>
              <InsertDriveFileIcon sx={{ fontSize: 24, color: "#059669" }} />
            </UploadIconBox>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "#059669",
                fontFamily: "'Sora', sans-serif",
                wordBreak: "break-all",
                px: 1,
              }}
            >
              {file.name}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "#9CA3AF",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              {(file.size / 1024 / 1024).toFixed(2)} MB · PDF · Click to change
            </Typography>
          </Box>
        ) : (
          /* Empty */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <UploadIconBox className="upload-icon-box" hasfile={0}>
              <CloudUploadIcon
                sx={{ fontSize: 24, color: "#2563EB", opacity: 0.75 }}
              />
            </UploadIconBox>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#0F1117",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Drop your PDF here
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "#9CA3AF",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              or click to browse · PDF only · max 20 MB
            </Typography>
          </Box>
        )}
      </PdfDropZone>

      {/* ── Action row ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
        sx={{ mt: 2 }}
      >
        {file && !loading && <ClearBtn onClick={handleClear}>Clear</ClearBtn>}
        <UploadBtn
          disabled={loading || !file}
          onClick={handleUpload}
          startIcon={
            loading ? (
              <Spinner />
            ) : (
              <CloudUploadIcon sx={{ fontSize: "16px !important" }} />
            )
          }
        >
          {loading ? "Uploading…" : "Upload"}
        </UploadBtn>
      </Stack>

      {/* ── Progress shimmer ── */}
      {loading && (
        <ProgressBar>
          <ProgressFill />
        </ProgressBar>
      )}
    </UploadWrapper>
  );
}
